import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Calculator, ShieldCheck, FileText, ArrowUpRight, Plus, Link2, HeartHandshake } from "lucide-react";

const META = {
  rc: { label: "Quotatore RC", icon: Calculator, color: "#10B981" },
  cau: { label: "Cauzioni Pro", icon: ShieldCheck, color: "#F97316" },
  mrv: { label: "Multirischi Volontariato", icon: HeartHandshake, color: "#0D9488" },
  mup: { label: "MUP", icon: FileText, color: "#1B2380" },
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short" }) : "");
const fmtEuro = (n) => (n == null ? "" : `€ ${Number(n).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`);

// currentModule: "rc" | "cau" | "mup" — nasconde la sua colonna e mostra le altre.
export default function ClienteCollegati({ currentModule, currentId, anagraficaId, nome, piva }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!anagraficaId && !nome) { setData({ rc: [], cau: [], mrv: [], mup: [] }); return; }
    const params = {};
    if (anagraficaId) params.anagrafica_id = anagraficaId;
    if (nome) params.nome = nome;
    if (piva) params.piva = piva;
    if (currentModule) params.exclude_module = currentModule;
    if (currentId) params.exclude_id = currentId;
    api.get("/hub/related", { params }).then((r) => setData(r.data)).catch(() => setData({ rc: [], cau: [], mrv: [], mup: [] }));
  }, [anagraficaId, nome, piva, currentModule, currentId]);

  const enteQ = anagraficaId ? `ente=${anagraficaId}&` : "";
  const nomeQ = nome ? `nome=${encodeURIComponent(nome)}` : "";
  const createLinks = {
    rc: `/quotatore-rc/preventivi/nuovo?${enteQ}${nomeQ}`,
    mup: `/mup/nuova?${enteQ}${nomeQ}`,
    cau: `/cauzioni-pro?nuovo=1&${enteQ}${nomeQ}`,
    mrv: `/multirischi-volontariato/preventivi/nuovo?${enteQ}${nomeQ}`,
  };
  const others = ["rc", "cau", "mrv", "mup"].filter((m) => m !== currentModule);

  return (
    <div className="rounded-sm border border-slate-200 bg-white p-4" data-testid="cliente-collegati">
      <div className="flex items-center gap-2 mb-3 text-slate-700">
        <Link2 className="h-4 w-4" />
        <span className="text-sm font-medium">Stesso cliente in altri moduli</span>
        {nome && <span className="text-xs text-slate-400">· {nome}</span>}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {others.map((m) => {
          const M = META[m]; const Icon = M.icon; const items = data?.[m] || [];
          return (
            <div key={m} className="rounded-sm border border-slate-100 overflow-hidden" data-testid={`collegati-col-${m}`}>
              <div className="flex items-center justify-between px-3 py-2" style={{ backgroundColor: `${M.color}0D` }}>
                <div className="flex items-center gap-1.5 text-sm font-medium" style={{ color: M.color }}>
                  <Icon className="h-4 w-4" /> {M.label} <span className="text-slate-400">({items.length})</span>
                </div>
                <button onClick={() => nav(createLinks[m])} data-testid={`crea-${m}`}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded hover:bg-white/70" style={{ color: M.color }}>
                  <Plus className="h-3 w-3" /> Crea
                </button>
              </div>
              <div className="divide-y divide-slate-50 max-h-40 overflow-y-auto">
                {!data && <div className="px-3 py-3 text-xs text-slate-400">Caricamento…</div>}
                {data && items.length === 0 && <div className="px-3 py-3 text-xs text-slate-400">Nessuna trattativa.</div>}
                {items.map((it) => (
                  <button key={it.id} onClick={() => nav(it.link)} data-testid={`collegato-${m}-${it.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-sm">
                    <span className="flex-1 min-w-0 truncate text-slate-700">{it.subtitle || "—"}</span>
                    {it.amount != null && <span className="text-xs text-slate-500 tabular-nums">{fmtEuro(it.amount)}</span>}
                    <span className="text-[11px] text-slate-400">{fmtDate(it.date)}</span>
                    <ArrowUpRight className="h-3.5 w-3.5 text-slate-300" />
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
